import React, { useState, useEffect } from "react";
import { Ledger, LedgerGroup, GROUP_NATURE } from "@/lib/accounting/types";
import { useAccounting } from "@/contexts/AccountingContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Pencil,
  AlertTriangle,
  ShieldCheck,
  Building2,
  Phone,
  Mail,
  MapPin,
  FileText,
  RotateCw,
  Hash,
  CheckCircle2,
  Info
} from "lucide-react";
import { toast } from "sonner";

const GROUPS = Object.keys(GROUP_NATURE) as LedgerGroup[];

const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand",
  "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur",
  "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab",
  "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura",
  "Uttar Pradesh", "Uttarakhand", "West Bengal", "Delhi", "Chandigarh",
  "Puducherry", "Jammu and Kashmir", "Ladakh"
];

const GST_RATES = [0, 5, 12, 18, 28];

const PROTECTED_SYSTEM_NAMES = new Set([
  "cash-in-hand",
  "bank account",
  "stock-in-hand / inventory asset",
  "cost of goods sold (cogs)",
  "sales account",
  "purchase account",
  "capital account",
  "cgst output (9%)",
  "sgst output (9%)",
  "igst output (18%)",
  "cgst input (9%)",
  "sgst input (9%)",
  "igst input (18%)",
  "round off"
]);

export interface EditLedgerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ledger: Ledger | null;
  onSaved?: (updated: Ledger) => void;
}

export const EditLedgerDialog: React.FC<EditLedgerDialogProps> = ({
  open,
  onOpenChange,
  ledger,
  onSaved,
}) => {
  const { ledgers, vouchers, updateLedger } = useAccounting();

  // Form Fields
  const [name, setName] = useState("");
  const [group, setGroup] = useState<LedgerGroup>("Sundry Debtors");
  const [openingBalance, setOpeningBalance] = useState<number>(0);
  const [balanceType, setBalanceType] = useState<"Dr" | "Cr">("Dr");
  const [gstApplicable, setGstApplicable] = useState(false);
  const [gstin, setGstin] = useState("");
  const [gstRate, setGstRate] = useState<string>("18");
  const [hsnCode, setHsnCode] = useState("");
  const [contactPerson, setContactPerson] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [state, setState] = useState("");
  const [pincode, setPincode] = useState("");

  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Pre-fill form when ledger changes or dialog opens
  useEffect(() => {
    if (ledger) {
      setName(ledger.name || "");
      setGroup(ledger.group || "Sundry Debtors");
      setOpeningBalance(Number(ledger.openingBalance || 0));
      setBalanceType(ledger.openingBalanceType || "Dr");
      setGstApplicable(Boolean(ledger.gstApplicable || ledger.gstin));
      setGstin(ledger.gstin || "");
      setGstRate(ledger.gstRate !== undefined && ledger.gstRate !== null ? String(ledger.gstRate) : "18");
      setHsnCode(ledger.hsnCode || "");
      setContactPerson(ledger.contactPerson || "");
      setPhone(ledger.phone || "");
      setEmail(ledger.email || "");
      setAddress(ledger.address || "");
      setState(ledger.state || "");
      setPincode(ledger.pincode || "");
      setErrorMessage(null);
    }
  }, [ledger, open]);

  if (!ledger) return null;

  // Determine existing transactions count
  const txCount = vouchers.filter((v) =>
    v.lines.some((l) => l.ledgerId === ledger.id)
  ).length;

  const isProtectedSystem =
    Boolean(ledger.isSystem) ||
    PROTECTED_SYSTEM_NAMES.has((ledger.name || "").trim().toLowerCase());

  // Can the account group be changed?
  // Block group changes if transactions exist or if it's a protected system ledger
  const isGroupChangeBlocked = txCount > 0 || isProtectedSystem;

  const handleSave = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setErrorMessage(null);

    const trimmedName = name.trim();
    if (!trimmedName) {
      setErrorMessage("Ledger account name is required.");
      return;
    }

    if (trimmedName.length > 100) {
      setErrorMessage("Ledger name must be 100 characters or less.");
      return;
    }

    // Duplicate name validation across same company
    const hasDuplicate = ledgers.some(
      (l) =>
        l.id !== ledger.id &&
        l.name.trim().toLowerCase() === trimmedName.toLowerCase()
    );
    if (hasDuplicate) {
      setErrorMessage(
        `A ledger account with the name "${trimmedName}" already exists in this company. Please choose a unique name.`
      );
      return;
    }

    // GSTIN format validation if GST is applicable
    if (gstApplicable && gstin.trim()) {
      const gstinVal = gstin.trim().toUpperCase();
      const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
      if (!gstinRegex.test(gstinVal)) {
        setErrorMessage("Please enter a valid 15-character GSTIN (e.g. 27AABCU9603R1ZM).");
        return;
      }
    }

    // PIN code validation
    if (pincode.trim() && !/^\d{6}$/.test(pincode.trim())) {
      setErrorMessage("PIN code must be a 6-digit number.");
      return;
    }

    // Phone validation
    if (phone.trim() && !/^[0-9+ -]{10,15}$/.test(phone.trim())) {
      setErrorMessage("Please enter a valid 10-digit phone number.");
      return;
    }

    // Email validation
    if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setErrorMessage("Please enter a valid email address.");
      return;
    }

    setSaving(true);
    try {
      const res = await updateLedger(ledger.id, {
        name: trimmedName,
        group: isGroupChangeBlocked ? ledger.group : group,
        nature: GROUP_NATURE[isGroupChangeBlocked ? ledger.group : group],
        openingBalance: Number(openingBalance) || 0,
        openingBalanceType: balanceType,
        gstApplicable,
        gstin: gstApplicable && gstin.trim() ? gstin.trim().toUpperCase() : undefined,
        gstRate: gstApplicable && gstRate ? Number(gstRate) : undefined,
        hsnCode: hsnCode.trim() || undefined,
        contactPerson: contactPerson.trim() || undefined,
        phone: phone.trim() || undefined,
        email: email.trim() || undefined,
        address: address.trim() || undefined,
        state: state.trim() || undefined,
        pincode: pincode.trim() || undefined,
      });

      if (!res.success) {
        throw new Error(res.error || "Failed to update ledger account");
      }

      toast.success(`Ledger "${trimmedName}" updated successfully!`, {
        description:
          trimmedName !== ledger.name
            ? "Ledger name updated across all vouchers, lines, and reports."
            : "Ledger account details saved.",
      });

      if (onSaved && res.ledger) {
        onSaved(res.ledger);
      }

      onOpenChange(false);
    } catch (err: any) {
      console.error("[EditLedgerDialog] Save error:", err);
      setErrorMessage(err.message || "Failed to update ledger account.");
      toast.error("Error updating ledger", {
        description: err.message || "Please check your entries and try again.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(val) => !saving && onOpenChange(val)}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto p-0 gap-0 border-slate-200">
        {/* Header */}
        <DialogHeader className="p-5 pb-4 border-b border-slate-100 bg-slate-50/70">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-100 border border-blue-200 text-blue-700 flex items-center justify-center shrink-0 shadow-2xs">
                <Pencil className="w-5 h-5" />
              </div>
              <div>
                <DialogTitle className="text-lg font-bold text-slate-900 flex items-center gap-2">
                  <span>Edit Ledger Account</span>
                  {isProtectedSystem && (
                    <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 border-blue-200 font-semibold gap-1">
                      <ShieldCheck className="w-3 h-3" /> System Account
                    </Badge>
                  )}
                </DialogTitle>
                <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
                  <span className="font-mono bg-slate-200/70 px-1.5 py-0.5 rounded text-[11px] text-slate-700">
                    ID: {ledger.id}
                  </span>
                  <span>•</span>
                  <span>Unique ID & double-entry relationships preserved</span>
                </div>
              </div>
            </div>
          </div>
        </DialogHeader>

        {/* Form Body */}
        <form onSubmit={handleSave} className="p-5 sm:p-6 space-y-5">
          {/* Error Banner */}
          {errorMessage && (
            <div className="p-3.5 rounded-xl bg-red-50 border border-red-200 flex items-start gap-2.5 text-xs text-red-800 animate-in fade-in">
              <AlertTriangle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
              <div className="flex-1 font-medium">{errorMessage}</div>
            </div>
          )}

          {/* Existing Transactions Safeguard Warning */}
          {txCount > 0 && (
            <div className="p-3.5 rounded-xl bg-amber-50/80 border border-amber-200/90 flex items-start gap-3 text-xs text-amber-900">
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <div className="space-y-1">
                <p className="font-semibold text-amber-950">
                  Active Transactions Safeguard ({txCount} voucher{txCount > 1 ? "s" : ""})
                </p>
                <p className="text-amber-800 leading-relaxed text-[11px]">
                  This ledger has {txCount} recorded transaction{txCount > 1 ? "s" : ""}. To preserve double-entry accounting integrity, the parent account group is locked. Correcting the opening balance will recalculate current running balances across reports.
                </p>
              </div>
            </div>
          )}

          {/* System Control Ledger Notice */}
          {isProtectedSystem && txCount === 0 && (
            <div className="p-3 rounded-xl bg-blue-50/80 border border-blue-200/90 flex items-start gap-2.5 text-xs text-blue-900">
              <Info className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
              <p className="text-[11px] text-blue-800 leading-relaxed">
                This is a designated system control account. The account group is locked to ensure statutory compliance and core ledger formulas remain intact.
              </p>
            </div>
          )}

          {/* Section 1: Basic Information */}
          <div className="space-y-4">
            <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-1.5 pb-1 border-b border-slate-100">
              <FileText className="w-3.5 h-3.5 text-blue-600" />
              General Account Details
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Ledger Name */}
              <div className="sm:col-span-2 space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="ledger-name" className="text-xs font-semibold text-slate-800">
                    Ledger Account Name <span className="text-red-500">*</span>
                  </Label>
                  <span className="text-[10px] text-slate-400">{name.length}/100</span>
                </div>
                <Input
                  id="ledger-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={100}
                  placeholder="e.g. Infosys Ltd / HDFC Bank"
                  className="h-9 text-sm border-slate-200 focus-visible:ring-blue-600"
                  required
                  autoFocus
                />
                <p className="text-[11px] text-slate-500">
                  Renaming will automatically update invoice views, vouchers, journals, and reports.
                </p>
              </div>

              {/* Parent Group */}
              <div className="space-y-1.5">
                <Label htmlFor="ledger-group" className="text-xs font-semibold text-slate-800">
                  Account Group {isGroupChangeBlocked && <span className="text-slate-400 font-normal">(Locked)</span>}
                </Label>
                <Select
                  value={isGroupChangeBlocked ? ledger.group : group}
                  onValueChange={(v) => setGroup(v as LedgerGroup)}
                  disabled={isGroupChangeBlocked}
                >
                  <SelectTrigger id="ledger-group" className={`h-9 text-sm ${isGroupChangeBlocked ? "bg-slate-100/80 cursor-not-allowed opacity-90" : "bg-white"}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {GROUPS.map((g) => (
                      <SelectItem key={g} value={g} className="text-xs">
                        {g} <span className="text-slate-400 text-[10px]">({GROUP_NATURE[g]})</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {isGroupChangeBlocked && (
                  <p className="text-[10px] text-slate-400">
                    {isProtectedSystem ? "Locked for system control ledgers." : "Locked because transactions exist."}
                  </p>
                )}
              </div>

              {/* Nature Badge Preview */}
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-800">Accounting Nature</Label>
                <div className="h-9 px-3 py-2 rounded-md bg-slate-50 border border-slate-200 flex items-center justify-between">
                  <span className="text-xs font-medium text-slate-700">Financial Classification</span>
                  <Badge variant="outline" className="text-[11px] font-bold bg-blue-50 text-blue-700 border-blue-200">
                    {GROUP_NATURE[isGroupChangeBlocked ? ledger.group : group]}
                  </Badge>
                </div>
              </div>
            </div>

            {/* Opening Balance & Type */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-1">
              <div className="sm:col-span-2 space-y-1.5">
                <Label htmlFor="opening-balance" className="text-xs font-semibold text-slate-800">
                  Opening Balance (₹)
                </Label>
                <Input
                  id="opening-balance"
                  type="number"
                  min="0"
                  step="any"
                  value={openingBalance}
                  onChange={(e) => setOpeningBalance(parseFloat(e.target.value) || 0)}
                  className="h-9 text-sm border-slate-200"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="balance-type" className="text-xs font-semibold text-slate-800">
                  Balance Type
                </Label>
                <Select value={balanceType} onValueChange={(v) => setBalanceType(v as "Dr" | "Cr")}>
                  <SelectTrigger id="balance-type" className="h-9 text-sm bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Dr" className="text-xs">Debit (Dr)</SelectItem>
                    <SelectItem value="Cr" className="text-xs">Credit (Cr)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Section 2: GST & Tax Details */}
          <div className="space-y-4 pt-2">
            <div className="flex items-center justify-between pb-1 border-b border-slate-100">
              <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-1.5">
                <Building2 className="w-3.5 h-3.5 text-blue-600" />
                GST & Tax Configuration
              </h3>
              <div className="flex items-center gap-2">
                <Label htmlFor="gst-toggle" className="text-xs text-slate-600 font-medium cursor-pointer">
                  GST Applicable
                </Label>
                <Switch
                  id="gst-toggle"
                  checked={gstApplicable}
                  onCheckedChange={setGstApplicable}
                />
              </div>
            </div>

            {gstApplicable && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 bg-slate-50/70 p-3.5 rounded-xl border border-slate-200 animate-in fade-in">
                {/* GSTIN */}
                <div className="sm:col-span-2 space-y-1">
                  <Label htmlFor="gstin" className="text-xs font-medium text-slate-700">
                    GSTIN (15 Digits)
                  </Label>
                  <Input
                    id="gstin"
                    value={gstin}
                    onChange={(e) => setGstin(e.target.value.toUpperCase())}
                    maxLength={15}
                    placeholder="27AABCU9603R1ZM"
                    className="h-8 text-xs font-mono uppercase bg-white border-slate-200"
                  />
                </div>

                {/* GST Rate */}
                <div className="space-y-1">
                  <Label htmlFor="gst-rate" className="text-xs font-medium text-slate-700">
                    Default GST Rate
                  </Label>
                  <Select value={gstRate} onValueChange={setGstRate}>
                    <SelectTrigger id="gst-rate" className="h-8 text-xs bg-white">
                      <SelectValue placeholder="Rate %" />
                    </SelectTrigger>
                    <SelectContent>
                      {GST_RATES.map((r) => (
                        <SelectItem key={r} value={String(r)} className="text-xs">
                          {r}% GST
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* HSN / SAC Code */}
                <div className="sm:col-span-3 space-y-1">
                  <Label htmlFor="hsn-code" className="text-xs font-medium text-slate-700">
                    HSN / SAC Code (Optional)
                  </Label>
                  <Input
                    id="hsn-code"
                    value={hsnCode}
                    onChange={(e) => setHsnCode(e.target.value)}
                    maxLength={10}
                    placeholder="e.g. 998311 or 8471"
                    className="h-8 text-xs bg-white border-slate-200"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Section 3: Contact & Address Information */}
          <div className="space-y-4 pt-2">
            <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-1.5 pb-1 border-b border-slate-100">
              <Phone className="w-3.5 h-3.5 text-blue-600" />
              Contact & Address Information
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
              <div className="space-y-1">
                <Label htmlFor="contact-person" className="text-xs font-medium text-slate-700">
                  Contact Person
                </Label>
                <Input
                  id="contact-person"
                  value={contactPerson}
                  onChange={(e) => setContactPerson(e.target.value)}
                  placeholder="e.g. Rajesh Kumar"
                  className="h-8 text-xs border-slate-200"
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="phone" className="text-xs font-medium text-slate-700">
                  Phone / Mobile
                </Label>
                <Input
                  id="phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="e.g. 9876543210"
                  className="h-8 text-xs border-slate-200"
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="email" className="text-xs font-medium text-slate-700">
                  Email Address
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="accounts@example.com"
                  className="h-8 text-xs border-slate-200"
                />
              </div>

              <div className="sm:col-span-3 space-y-1">
                <Label htmlFor="address" className="text-xs font-medium text-slate-700">
                  Billing / Office Address
                </Label>
                <Textarea
                  id="address"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  rows={2}
                  placeholder="Street, building, area..."
                  className="text-xs border-slate-200 resize-none"
                />
              </div>

              <div className="sm:col-span-2 space-y-1">
                <Label htmlFor="state" className="text-xs font-medium text-slate-700">
                  State / Territory
                </Label>
                <Select value={state} onValueChange={setState}>
                  <SelectTrigger id="state" className="h-8 text-xs bg-white">
                    <SelectValue placeholder="Select State" />
                  </SelectTrigger>
                  <SelectContent className="max-h-56">
                    {INDIAN_STATES.map((s) => (
                      <SelectItem key={s} value={s} className="text-xs">
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <Label htmlFor="pincode" className="text-xs font-medium text-slate-700">
                  PIN Code
                </Label>
                <Input
                  id="pincode"
                  value={pincode}
                  onChange={(e) => setPincode(e.target.value)}
                  maxLength={6}
                  placeholder="400001"
                  className="h-8 text-xs border-slate-200 font-mono"
                />
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          <DialogFooter className="pt-4 border-t border-slate-100 flex items-center justify-between sm:justify-between w-full">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={saving}
              className="text-xs h-9 text-slate-600 hover:text-slate-900 border-slate-200"
            >
              Cancel
            </Button>

            <Button
              type="submit"
              disabled={saving}
              className="text-xs h-9 bg-blue-600 text-white hover:bg-blue-700 font-semibold gap-1.5 shadow-2xs px-4"
            >
              {saving ? (
                <>
                  <RotateCw className="w-3.5 h-3.5 animate-spin" />
                  <span>Saving Changes...</span>
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  <span>Save Changes</span>
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
